/**
 * GlobalFilterContext
 * Persistent cross-view filters, adapted from fast-cortex-orderbook.
 *
 * The fixed Account Manager / Project Manager / Sales Code trio is replaced by a
 * tenant-configurable filter slot: the provider takes a `slots` manifest
 * (id + api_param per slot) and exposes generic get/set by slot id. Cortex ships
 * with no slots at S0 — a tenant that needs cross-view filters declares them in
 * config, no context change required (CD-4 spirit: behaviour is data, not code).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const GlobalFilterContext = createContext()

const STORAGE_KEY = 'cortex_global_filters'

// Tenant filter-slot manifest. Example slot:
//   { id: 'accountManager', api_param: 'account_manager_id', label: 'Account Manager' }
export const TENANT_FILTER_SLOTS = []

const defaultsFor = (slots) =>
  Object.fromEntries(slots.map((s) => [s.id, null]))

export const GlobalFilterProvider = ({ slots = TENANT_FILTER_SLOTS, children }) => {
  const [filters, setFilters] = useState(() => {
    const defaults = defaultsFor(slots)
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored ? { ...defaults, ...JSON.parse(stored) } : defaults
    } catch {
      return defaults
    }
  })

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  }, [filters])

  const setFilter = useCallback((slotId, value) => {
    setFilters((prev) => ({ ...prev, [slotId]: value || null }))
  }, [])

  const clearAll = useCallback(() => {
    setFilters(defaultsFor(slots))
  }, [slots])

  const getApiFilters = useCallback(
    () =>
      Object.fromEntries(
        slots.map((s) => [s.api_param, filters[s.id] ?? null])
      ),
    [slots, filters]
  )

  const hasActiveFilters = slots.some((s) => filters[s.id])

  return (
    <GlobalFilterContext.Provider value={{
      slots,
      filters,
      setFilter,
      clearAll,
      getApiFilters,
      hasActiveFilters,
    }}>
      {children}
    </GlobalFilterContext.Provider>
  )
}

export const useGlobalFilters = () => {
  const context = useContext(GlobalFilterContext)
  if (!context) {
    throw new Error('useGlobalFilters must be used within a GlobalFilterProvider')
  }
  return context
}
